import React, {useCallback, useState} from 'react';

import {Permissions} from 'auth/PermissionConstants';
import {HistoryState, IndexSlice} from 'notebook/ELNConstants';
import SubtemplateActions from 'notebook/entry-templates/SubtemplateActions';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import SubtemplateUtil from 'notebook/entry-templates/SubtemplateUtil';
import {CidIdMap, PRINT_OPTION} from 'notebook/notes/NoteConstants';
import {NoteView} from 'notebook/notes/tabs/NoteViews';
// @ts-expect-error
import HistoryManager from 'notebook/store/HistoryManager';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {MultidocTableOpMap} from 'notebook/tables/excess/ops/TableOpConstants';
import {BioentityNote, Note} from 'notebook/util/NoteUtilConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface SubtemplateNoteViewProps {
  banner: string | React.ReactNode;
  fileId: string;
  isLatest: boolean;
  allowEdits: boolean;
  printOptions?: Array<string>;
  lockedFromExternalChange: boolean;
  showTimestamps: boolean;
  isViewingPublishedVersion?: boolean;
  fileType: string;
  note: Note;
  showTableOfContents: boolean;
  spellCheck: boolean;
  printLayout: boolean;
  showPrintMargins: boolean;
  permissions: Permissions;
  parentType: string;
  titleBadge?: React.ReactNode;
  title?: string;
}

export const SUBTEMPLATE_VIEW_PRINT_OPTIONS = [PRINT_OPTION.NOTE, PRINT_OPTION.SELECTION];

export default function SubtemplateNoteView(props: SubtemplateNoteViewProps): JSX.Element {
  const {
    fileId,
    isLatest,
    allowEdits,
    printOptions,
    lockedFromExternalChange,
    showTimestamps,
    isViewingPublishedVersion,
  } = props;

  const [historyManager, _] = useState(new HistoryManager());

  const printHandler = useCallback(
    (printType: PRINT_OPTION, indexSlice?: IndexSlice | null): void => {
      if (indexSlice == null) {
        indexSlice = null;
      }
      switch (printType) {
        case PRINT_OPTION.NOTE:
          SubtemplateActions.print(fileId, {
            showNote: true,
            showTimestamps,
          });
          break;
        case PRINT_OPTION.SELECTION:
          SubtemplateActions.print(fileId, {
            showNote: true,
            showTimestamps,
            indexSlice,
          });
          break;
        default:
          throw new Error(`Unexpected print handler type ${printType}`);
      }
    },
    [fileId, showTimestamps]
  );

  const restoreStateAndSelection = useCallback(
    ({note, title}: HistoryState<BioentityNote>, selectionData: SelectionRestoreData): void => {
      SubtemplateActions.revert(
        fileId,
        {name: {$set: title}, ...SubtemplateUtil.noteVersionUpdate({$merge: note})},
        selectionData
      );
    },
    [fileId]
  );

  const registerObjects = useCallback(
    (cidIdMap: CidIdMap): void => {
      SubtemplateActions.registerObjects(fileId, cidIdMap);
    },
    [fileId]
  );

  const getIdFromCid = useCallback(
    (cid: string): string | number | undefined => SubtemplateStore.getIdFromCid(fileId, cid),
    [fileId]
  );

  const updateNote = useCallback(
    (
      note: BioentityNote,
      opts?: {performTableEvaluation: boolean; multidocTableOp?: MultidocTableOpMap},
      structuredTableMappings?: Array<StructuredTableMapping>
    ): void => {
      SubtemplateActions.updateNote(fileId, note, structuredTableMappings, {
        performTableEvaluation: opts?.performTableEvaluation,
        multidocTableOp: opts?.multidocTableOp,
      });
    },
    [fileId]
  );

  const updateTitle = useCallback(
    (name: string): void => {
      SubtemplateActions.setName(fileId, name);
    },
    [fileId]
  );

  return (
    <NoteView
      {...props}
      getIdFromCid={getIdFromCid}
      printHandler={printHandler}
      registerObjects={registerObjects}
      updateNote={updateNote}
      updateTitle={updateTitle}
      historyManager={historyManager}
      locked={isViewingPublishedVersion || !isLatest || !allowEdits}
      lockedFromExternalChange={lockedFromExternalChange}
      restoreStateAndSelection={restoreStateAndSelection}
      printOptions={printOptions}
      editorIcon={
        <Tooltip text="Sub-template">
          <BenchlingIcon icon="subtemplate" />
        </Tooltip>
      }
    />
  );
}
